import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import api from '../../lib/api';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';

const PrimesTab = () => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState('list'); // list, create, upload, review, distribute
  const [campagnes, setCampagnes] = useState([]);
  const [campagneActive, setCampagneActive] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Nouvelle campagne
  const [nouvelleCampagne, setNouvelleCampagne] = useState({
    nom: '',
    type_campagne: 'RAMADAN',
    type_campagne_personnalise: '', // Pour type personnalisé
    description: '',
    mode_distribution: 'IMPORT_EXCEL'
  });
  
  const [showTypePersonnalise, setShowTypePersonnalise] = useState(false);
  
  // Données upload
  const [primes, setPrimes] = useState([]);
  const [resume, setResume] = useState(null);
  const [uploadErrors, setUploadErrors] = useState([]);

  useEffect(() => {
    loadCampagnes();
  }, []);

  const loadCampagnes = async () => {
    try {
      setLoading(true);
      const response = await api.get('/primes/campagnes');
      setCampagnes(response.data.campagnes || []);
    } catch (error) {
      console.error('Erreur chargement campagnes:', error);
      toast.error('Erreur lors du chargement des campagnes');
    } finally {
      setLoading(false);
    }
  };

  const formatTypePrime = (type) => {
    // Types prédéfinis avec emojis
    const typesPredefinis = {
      'RAMADAN': '🌙 Ramadan',
      'NOEL': '🎄 Noël / Fin d\'année',
      'SCOLARITE': '📚 Scolarité',
      'PERFORMANCE': '🏆 Performance',
      'EVENEMENT': '🎉 Événement',
      'AUTRE': '📌 Autre'
    };

    // Si c'est un type prédéfini, retourner avec emoji
    if (typesPredefinis[type]) {
      return typesPredefinis[type];
    }

    // Sinon, formater le type personnalisé (remplacer _ par espaces et capitaliser)
    return '✨ ' + type.replace(/_/g, ' ')
      .toLowerCase()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  const formatCurrency = (amount) => {
    return `${Math.round(amount || 0).toLocaleString('fr-FR')} GNF`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatutBadge = (statut) => {
    const styles = {
      'BROUILLON': 'bg-gray-100 text-gray-700',
      'EN_ATTENTE_VALIDATION': 'bg-yellow-100 text-yellow-700',
      'VALIDEE': 'bg-blue-100 text-blue-700',
      'DISTRIBUEE': 'bg-green-100 text-green-700',
      'REJETEE': 'bg-red-100 text-red-700'
    };
    
    const labels = {
      'BROUILLON': '📝 Brouillon',
      'EN_ATTENTE_VALIDATION': '⏳ En attente validation',
      'VALIDEE': '✅ Validée',
      'DISTRIBUEE': '💰 Distribuée',
      'REJETEE': '❌ Rejetée'
    };

    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${styles[statut] || 'bg-gray-100 text-gray-700'}`}>
        {labels[statut] || statut}
      </span>
    );
  };

  const handleCreerCampagne = async () => {
    if (!nouvelleCampagne.nom.trim()) {
      toast.warning('Veuillez saisir un nom de campagne');
      return;
    }

    // Si type personnalisé est sélectionné, utiliser la valeur du champ personnalisé
    const typeCampagne = showTypePersonnalise 
      ? nouvelleCampagne.type_campagne_personnalise.trim().toUpperCase().replace(/\s+/g, '_')
      : nouvelleCampagne.type_campagne;

    if (showTypePersonnalise && !nouvelleCampagne.type_campagne_personnalise.trim()) {
      toast.warning('Veuillez saisir le type de prime personnalisé');
      return;
    }

    try {
      const campagneData = {
        nom: nouvelleCampagne.nom,
        type_campagne: typeCampagne,
        description: nouvelleCampagne.description,
        mode_distribution: nouvelleCampagne.mode_distribution
      };

      console.log('Envoi données:', campagneData);

      const response = await api.post('/primes/campagne/create', campagneData);
      toast.success('Campagne créée avec succès !');
      
      setCampagneActive(response.data.campagne_id);
      setActiveStep('upload');
      await loadCampagnes();
    } catch (error) {
      console.error('Erreur création campagne:', error);
      console.error('Détails erreur:', error.response?.data);
      const errorMsg = error.response?.data?.detail || error.message || 'Erreur lors de la création';
      toast.error(errorMsg);
    }
  };

  const handleDownloadTemplate = async (campagneId) => {
    try {
      toast.info('Téléchargement du template en cours...');
      
      const response = await api.get(`/primes/campagne/${campagneId}/template-excel`, {
        responseType: 'blob'
      });
      
      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Template_Primes_${campagneId}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Template téléchargé !');
    } catch (error) {
      console.error('Erreur téléchargement template:', error);
      toast.error('Erreur lors du téléchargement du template');
    }
  };

  const handleUploadExcel = async (campagneId, file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      toast.info('Validation du fichier en cours...');
      
      const response = await api.post(`/primes/campagne/${campagneId}/upload-excel`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.success) {
        toast.success(response.data.message);
        setPrimes(response.data.primes || []);
        setResume(response.data.resume || null);
        setUploadErrors([]);
        setActiveStep('review');
      } else {
        toast.error(response.data.message);
        setUploadErrors(response.data.erreurs || []);
      }
    } catch (error) {
      console.error('Erreur upload Excel:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
    }
  };

  const handleDistribuer = async (campagneId) => {
    if (!window.confirm('Confirmer la distribution des primes ? Cette action est IRRÉVERSIBLE.')) {
      return;
    }

    try {
      const response = await api.post(`/primes/campagne/${campagneId}/distribuer`);
      
      if (response.data.statut === 'EN_ATTENTE_VALIDATION') {
        toast.info('Campagne soumise pour validation Super Admin (mode CREDIT)');
      } else {
        toast.success('Primes distribuées avec succès !');
      }
      
      setActiveStep('list');
      setCampagneActive(null);
      setPrimes([]);
      setResume(null);
      await loadCampagnes();
    } catch (error) {
      console.error('Erreur distribution:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la distribution');
    }
  };

  // ========== VUE LISTE ==========
  if (activeStep === 'list') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-900">🎁 Gestion des Primes</h3>
          <button
            onClick={() => setActiveStep('create')}
            className="px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 transition-colors"
          >
            ➕ Nouvelle Campagne
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
          </div>
        ) : campagnes.length === 0 ? (
          <Card>
            <CardContent>
              <div className="text-center py-12">
                <div className="text-6xl mb-4">🎁</div>
                <p className="text-gray-500 text-lg">Aucune campagne de primes</p>
                <p className="text-gray-400 text-sm mt-2">Créez votre première campagne pour commencer</p>
              </div>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {campagnes.map(campagne => (
              <Card key={campagne.id} className="hover:shadow-lg transition-shadow">
                <CardContent>
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="text-lg font-bold text-gray-900">{campagne.nom}</h4>
                        {getStatutBadge(campagne.statut)}
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 mt-4">
                        <div>
                          <p className="text-sm text-gray-600">Type</p>
                          <p className="font-medium">{formatTypePrime(campagne.type_campagne)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Montant Total</p>
                          <p className="font-medium text-violet-600">{formatCurrency(campagne.montant_total)}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Employés</p>
                          <p className="font-medium">{campagne.nb_employes_inclus || 0}</p>
                        </div>
                        <div>
                          <p className="text-sm text-gray-600">Date création</p>
                          <p className="font-medium">{formatDate(campagne.created_at)}</p>
                        </div>
                      </div>

                      {campagne.description && (
                        <p className="text-sm text-gray-600 mt-3 italic">"{campagne.description}"</p>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 ml-4">
                      {campagne.statut === 'BROUILLON' && (
                        <>
                          <button
                            onClick={() => {
                              setCampagneActive(campagne.id);
                              setActiveStep('upload');
                            }}
                            className="px-4 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                          >
                            📤 Continuer
                          </button>
                        </>
                      )}
                      {campagne.statut === 'EN_ATTENTE_VALIDATION' && user?.role === 'SUPER_ADMIN' && (
                        <button
                          className="px-4 py-2 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        >
                          ✅ Valider
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    );
  }

  // ========== VUE CRÉATION ==========
  if (activeStep === 'create') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveStep('list')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Retour
          </button>
          <h3 className="text-xl font-bold text-gray-900">Nouvelle Campagne de Primes</h3>
        </div>

        <Card>
          <CardContent>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nom de la campagne *
                </label>
                <input
                  type="text"
                  value={nouvelleCampagne.nom}
                  onChange={(e) => setNouvelleCampagne({...nouvelleCampagne, nom: e.target.value})}
                  placeholder="Ex: Prime Ramadan 2025"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type de prime *
                </label>
                
                {/* Choix : Liste prédéfinie ou personnalisé */}
                <div className="mb-3">
                  <div className="flex gap-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={!showTypePersonnalise}
                        onChange={() => setShowTypePersonnalise(false)}
                        className="mr-2"
                      />
                      <span className="text-sm">Type prédéfini</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="radio"
                        checked={showTypePersonnalise}
                        onChange={() => setShowTypePersonnalise(true)}
                        className="mr-2"
                      />
                      <span className="text-sm">Type personnalisé</span>
                    </label>
                  </div>
                </div>

                {/* Liste déroulante si type prédéfini */}
                {!showTypePersonnalise ? (
                  <select
                    value={nouvelleCampagne.type_campagne}
                    onChange={(e) => setNouvelleCampagne({...nouvelleCampagne, type_campagne: e.target.value})}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                  >
                    <option value="RAMADAN">Ramadan</option>
                    <option value="NOEL">Noël / Fin d'année</option>
                    <option value="SCOLARITE">Scolarité</option>
                    <option value="PERFORMANCE">Performance</option>
                    <option value="EVENEMENT">Événement (mariage, naissance)</option>
                    <option value="AUTRE">Autre</option>
                  </select>
                ) : (
                  /* Champ texte si type personnalisé */
                  <div>
                    <input
                      type="text"
                      value={nouvelleCampagne.type_campagne_personnalise}
                      onChange={(e) => setNouvelleCampagne({...nouvelleCampagne, type_campagne_personnalise: e.target.value})}
                      placeholder="Ex: Prime de productivité, Prime transport, Prime d'ancienneté..."
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      💡 Vous pouvez créer votre propre type de prime adapté à votre entreprise
                    </p>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description (optionnelle)
                </label>
                <textarea
                  value={nouvelleCampagne.description}
                  onChange={(e) => setNouvelleCampagne({...nouvelleCampagne, description: e.target.value})}
                  placeholder="Ex: Prime exceptionnelle Ramadan 1446"
                  rows="3"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-transparent"
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-2">📋 Mode de distribution</h4>
                <p className="text-sm text-blue-700">
                  Les primes seront distribuées via <strong>Import Excel personnalisé</strong>.
                  Chaque employé pourra avoir un montant différent selon vos critères.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => setActiveStep('list')}
                  className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreerCampagne}
                  className="flex-1 px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
                >
                  Créer la campagne →
                </button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========== VUE UPLOAD ==========
  if (activeStep === 'upload') {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              setActiveStep('list');
              setCampagneActive(null);
            }}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Retour
          </button>
          <h3 className="text-xl font-bold text-gray-900">Import Excel - Primes personnalisées</h3>
        </div>

        <Card>
          <CardContent>
            <div className="space-y-6">
              <div className="bg-violet-50 border border-violet-200 rounded-lg p-4">
                <h4 className="font-medium text-violet-900 mb-2">📥 Étape 1 : Télécharger le modèle Excel</h4>
                <p className="text-sm text-violet-700 mb-4">
                  Le modèle contient la liste de vos employés actifs avec les colonnes pré-remplies.
                </p>
                <button
                  onClick={() => handleDownloadTemplate(campagneActive)}
                  className="w-full px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
                >
                  ⬇️ Télécharger le Modèle Excel
                </button>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">✏️ Étape 2 : Compléter le fichier</h4>
                <ul className="text-sm text-gray-700 space-y-2">
                  <li>• Remplissez la colonne <strong>montant_prime</strong> pour chaque employé</li>
                  <li>• Mettez <strong>OUI</strong> ou <strong>NON</strong> dans la colonne <strong>inclure</strong></li>
                  <li>• Ne modifiez pas les autres colonnes (matricule, nom, prénom)</li>
                  <li>• Enregistrez le fichier</li>
                </ul>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-4">📤 Étape 3 : Importer le fichier complété</h4>
                <input
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      handleUploadExcel(campagneActive, file);
                    }
                  }}
                  className="w-full px-4 py-3 border-2 border-dashed border-blue-300 rounded-lg cursor-pointer hover:border-blue-500 hover:bg-blue-100 transition-colors"
                />
                <p className="text-xs text-blue-600 mt-2">
                  Format accepté : .xlsx, .xls (max 5 MB)
                </p>
              </div>

              {uploadErrors.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <h4 className="font-medium text-red-900 mb-3">⚠️ Erreurs trouvées ({uploadErrors.length})</h4>
                  <div className="max-h-64 overflow-y-auto space-y-2">
                    {uploadErrors.map((err, idx) => (
                      <div key={idx} className="text-sm text-red-700 bg-white p-3 rounded">
                        <strong>Ligne {err.ligne}</strong> ({err.employe}) : {err.erreur}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ========== VUE RÉVISION ==========
  if (activeStep === 'review') {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setActiveStep('upload')}
            className="text-gray-600 hover:text-gray-900"
          >
            ← Retour
          </button>
          <h3 className="text-xl font-bold text-gray-900">Révision Finale</h3>
        </div>

        {/* Résumé */}
        {resume && (
          <Card>
            <CardHeader>
              <CardTitle>📊 Résumé de la distribution</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-4 gap-4">
                <div className="bg-violet-50 p-4 rounded-lg">
                  <p className="text-sm text-violet-600 mb-1">Employés inclus</p>
                  <p className="text-2xl font-bold text-violet-900">{resume.nb_employes_inclus}</p>
                </div>
                <div className="bg-green-50 p-4 rounded-lg">
                  <p className="text-sm text-green-600 mb-1">Montant Total</p>
                  <p className="text-2xl font-bold text-green-900">{formatCurrency(resume.montant_total)}</p>
                </div>
                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-600 mb-1">Prime Moyenne</p>
                  <p className="text-2xl font-bold text-blue-900">{formatCurrency(resume.prime_moyenne)}</p>
                </div>
                <div className="bg-orange-50 p-4 rounded-lg">
                  <p className="text-sm text-orange-600 mb-1">Min / Max</p>
                  <p className="text-lg font-bold text-orange-900">
                    {formatCurrency(resume.prime_min)} / {formatCurrency(resume.prime_max)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Liste des primes */}
        <Card>
          <CardHeader>
            <CardTitle>📋 Liste des primes ({primes.length} employés)</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[480px]">
                <thead className="bg-gray-50 sticky top-0">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Matricule</th>
                    <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Nom Complet</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Salaire Net</th>
                    <th className="px-4 py-3 text-right text-sm font-medium text-gray-700">Prime</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {primes.map((prime, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm">{prime.matricule}</td>
                      <td className="px-4 py-3 text-sm font-medium">{prime.prenom} {prime.nom}</td>
                      <td className="px-4 py-3 text-sm text-right text-gray-600">{formatCurrency(prime.salaire_net)}</td>
                      <td className="px-4 py-3 text-sm text-right font-bold text-violet-600">{formatCurrency(prime.montant_prime)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Confirmation */}
        <Card>
          <CardContent>
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
              <h4 className="font-medium text-yellow-900 mb-2">⚠️ Attention</h4>
              <p className="text-sm text-yellow-700">
                Une fois les primes distribuées, elles deviennent <strong>IRRÉVOCABLES</strong>.
                Les employés pourront retirer leur argent à tout moment.
              </p>
            </div>
            
            <div className="flex gap-3">
              <button
                onClick={() => setActiveStep('upload')}
                className="flex-1 px-4 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                ← Modifier
              </button>
              <button
                onClick={() => handleDistribuer(campagneActive)}
                className="flex-1 px-4 py-3 bg-violet-600 text-white rounded-lg hover:bg-violet-700 font-medium"
              >
                🚀 Distribuer les Primes
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default PrimesTab;
